/**
 * Pathfinding utilities for custom indoor navigation.
 * Implements Dijkstra's algorithm for finding shortest paths between markers.
 */

// Types
export interface PathNode {
  id: string;
  name?: string; // Optional human-readable name for the waypoint (e.g., "Near Elevator B")
  spaceId?: string; // Optional - only set for nodes linked to a space/marker
  overlayId?: string; // Optional - only set for nodes linked to an overlay (without space)
  position: { x: number; y: number };
  layerId: number;
}

export interface PathEdge {
  id: string;
  from: string; // node id
  to: string; // node id
  bidirectional: boolean;
  weight?: number; // Optional custom weight; if not provided, calculated from distance
}

export interface PathNetwork {
  nodes: PathNode[];
  edges: PathEdge[];
}

export interface PathResult {
  found: boolean;
  path: string[]; // Array of node IDs
  distance: number;
  positions?: Array<{ x: number; y: number }>; // Positions for drawing
}

export interface PathfindingOptions {
  layerId?: number; // Filter to specific layer
}

// Graph representation for Dijkstra's algorithm
export interface GraphEdge {
  nodeId: string;
  weight: number;
}

export type Graph = Map<string, GraphEdge[]>;

/**
 * Calculate Euclidean distance between two points
 */
export function calculateDistance(
  p1: { x: number; y: number },
  p2: { x: number; y: number }
): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Build an adjacency list graph from the path network
 */
export function buildGraph(network: PathNetwork, layerId?: number): Graph {
  const graph: Graph = new Map();

  // Filter nodes by layer if specified
  const nodes = layerId !== undefined
    ? network.nodes.filter((n) => n.layerId === layerId)
    : network.nodes;

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  // Initialize all nodes in the graph
  for (const node of nodes) {
    graph.set(node.id, []);
  }

  // Add edges
  for (const edge of network.edges) {
    const fromNode = nodeMap.get(edge.from);
    const toNode = nodeMap.get(edge.to);

    // Skip edges that reference nodes not in our filtered set
    if (!fromNode || !toNode) continue;

    // Calculate weight from distance if not provided
    const weight = edge.weight ?? calculateDistance(fromNode.position, toNode.position);

    // Add forward edge
    graph.get(edge.from)!.push({ nodeId: edge.to, weight });

    // Add reverse edge if bidirectional
    if (edge.bidirectional) {
      graph.get(edge.to)!.push({ nodeId: edge.from, weight });
    }
  }

  return graph;
}

/**
 * Find the shortest path between two nodes using Dijkstra's algorithm
 */
export function findShortestPath(
  network: PathNetwork,
  startId: string,
  endId: string,
  options: PathfindingOptions = {}
): PathResult {
  const { layerId } = options;

  const layerNodes = network.nodes.filter((n) => n.layerId === layerId);

  // Handle empty network
  if (layerNodes.length === 0) {
    return { found: false, path: [], distance: Infinity };
  }

  // Build the graph
  const graph = buildGraph(network, layerId);

  // Check if start and end nodes exist in the graph
  if (!graph.has(startId) || !graph.has(endId)) {
    return { found: false, path: [], distance: Infinity };
  }

  // Handle same start and end
  if (startId === endId) {
    const node = layerNodes.find((n) => n.id === startId);
    return {
      found: true,
      path: [startId],
      distance: 0,
      positions: node ? [node.position] : [],
    };
  }

  // Dijkstra's algorithm
  const distances = new Map<string, number>();
  const previous = new Map<string, string | null>();
  const unvisited = new Set<string>();

  // Initialize distances
  for (const nodeId of graph.keys()) {
    distances.set(nodeId, nodeId === startId ? 0 : Infinity);
    previous.set(nodeId, null);
    unvisited.add(nodeId);
  }

  while (unvisited.size > 0) {
    // Find unvisited node with smallest distance
    let currentNode: string | null = null;
    let smallestDistance = Infinity;

    for (const nodeId of unvisited) {
      const dist = distances.get(nodeId)!;
      if (dist < smallestDistance) {
        smallestDistance = dist;
        currentNode = nodeId;
      }
    }

    // If we can't reach any more nodes, break
    if (currentNode === null || smallestDistance === Infinity) {
      break;
    }

    // If we've reached the destination, we're done
    if (currentNode === endId) {
      break;
    }

    // Remove current node from unvisited
    unvisited.delete(currentNode);

    // Update distances to neighbors
    const neighbors = graph.get(currentNode) || [];
    for (const { nodeId: neighborId, weight } of neighbors) {
      if (!unvisited.has(neighborId)) continue;

      const newDistance = distances.get(currentNode)! + weight;
      if (newDistance < distances.get(neighborId)!) {
        distances.set(neighborId, newDistance);
        previous.set(neighborId, currentNode);
      }
    }
  }

  // Check if we found a path
  const finalDistance = distances.get(endId)!;
  if (finalDistance === Infinity) {
    return { found: false, path: [], distance: Infinity };
  }

  // Reconstruct path
  const path: string[] = [];
  let current: string | null = endId;

  while (current !== null) {
    path.unshift(current);
    current = previous.get(current) || null;
  }

  // Get positions for the path
  const nodeMap = new Map(layerNodes.map((n) => [n.id, n]));
  const positions = path.map((nodeId) => nodeMap.get(nodeId)!.position);

  return {
    found: true,
    path,
    distance: finalDistance,
    positions,
  };
}

/**
 * Get all edges as line segments for rendering
 */
export function getPathSegments(
  network: PathNetwork,
  layerId?: number
): Array<{ from: { x: number; y: number }; to: { x: number; y: number }; edgeId: string }> {
  const nodeMap = new Map(network.nodes.map((n) => [n.id, n]));
  const segments: Array<{ from: { x: number; y: number }; to: { x: number; y: number }; edgeId: string }> = [];

  for (const edge of network.edges) {
    const fromNode = nodeMap.get(edge.from);
    const toNode = nodeMap.get(edge.to);

    if (!fromNode || !toNode) continue;

    // Filter by layer if specified
    if (layerId !== undefined && (fromNode.layerId !== layerId || toNode.layerId !== layerId)) {
      continue;
    }

    segments.push({
      from: fromNode.position,
      to: toNode.position,
      edgeId: edge.id,
    });
  }

  return segments;
}
